import localStore from '@theme/local-store'
import { Component } from '@theme/component'

/**
 * <filter-link>
 * Envuelve un enlace (<a>) y, siguiendo la lógica de `vehicle-summary`,
 * lee el vehículo seleccionado desde localStore para añadir su `handle`
 * (query de filtro) al `href` del link.
 *
 * Uso esperado en Liquid:
 *   <filter-link>
 *     <a href="/collections/alguna-coleccion">Texto</a>
 *   </filter-link>
 */
class FilterLink extends Component {
  constructor() {
    super()
    /** @type {(e: CustomEvent) => void} */
    this._onVehicleChanged = this._onVehicleChanged.bind(this)
    /** @type {(e: CustomEvent) => void} */
    this._onMakeChanged = this._onMakeChanged.bind(this)

    /** @type {HTMLAnchorElement | null} */
    this._anchor = null
    /** @type {string | null} */
    this._baseHref = null
  }

  connectedCallback() {
    super.connectedCallback?.()

    // Busca el primer <a href> dentro del componente
    this._anchor = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('a[href]')
    )
    if (this._anchor) {
      // Guardar href original para evitar duplicados al re-renderizar
      this._baseHref = this._anchor.getAttribute('href') || this._anchor.href || ''
    }

    requestAnimationFrame(() => this._render())
    document.addEventListener('vehicle:changed', this._onVehicleChanged)
    document.addEventListener('vehicle-make:changed', this._onMakeChanged)
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:changed', this._onVehicleChanged)
    document.removeEventListener('vehicle-make:changed', this._onMakeChanged)
    super.disconnectedCallback?.()
  }

  /** @param {CustomEvent} _e */
  _onVehicleChanged(_e) {
    this._render()
  }

  /** @param {CustomEvent} _e */
  _onMakeChanged(_e) {
    // Si cambia el make y no hay vehículo seleccionado, resetea el link
    this._render()
  }

  _render() {
    if (!this._anchor) return

    const baseHref = this._baseHref || this._anchor.getAttribute('href') || ''
    const vehicle = this._readStoredVehicle()
    const handleQuery = vehicle?.handle || '' // Ej: "?filter.p.m.vehicles.vehicle=gid%3A..."

    this._anchor.setAttribute('href', this._composeHref(baseHref, handleQuery))
  }

  /** Lee la selección de vehículo guardada en localStore (puede venir como string u objeto) */
  _readStoredVehicle() {
    try {
      let stored = localStore.get('vehicle', null)
      if (!stored) return null
      if (typeof stored === 'string') {
        try {
          stored = JSON.parse(stored)
        } catch (_) {}
      }
      return stored && typeof stored === 'object' ? stored : null
    } catch (_) {
      return null
    }
  }

  /**
   * Compone el href final sumando el query del handle del vehículo al href base.
   * Conserva cualquier hash existente y evita duplicados reusando el href base original.
   *
   * @param {string} baseHref
   * @param {string} handleQuery // Puede iniciar con '?'
   */
  _composeHref(baseHref, handleQuery) {
    if (!baseHref) return ''

    const queryPart = String(handleQuery || '').trim()
    if (!queryPart) return baseHref

    // Separar hash si existe
    const hashIndex = baseHref.indexOf('#')
    const hasHash = hashIndex !== -1
    const pathAndQuery = hasHash ? baseHref.slice(0, hashIndex) : baseHref
    const hash = hasHash ? baseHref.slice(hashIndex) : ''

    // Limpiar prefijo '?' del handle para componer correctamente
    const qp = queryPart.startsWith('?') ? queryPart.slice(1) : queryPart
    if (!qp) return baseHref

    const joiner = pathAndQuery.includes('?') ? '&' : '?'
    const composed = `${pathAndQuery}${joiner}${qp}`
    return composed + hash
  }
}

if (!customElements.get('filter-link')) {
  customElements.define('filter-link', FilterLink)
}

